'use client'

import { useRef, useCallback, useEffect } from 'react'
import clsx from 'clsx'
import { ReviewCardData } from '@/lib/google-reviews/types'
import { GoogleReviewCard } from './GoogleReviewCard'

interface ReviewMarqueeProps {
  reviews: ReviewCardData[]
  direction?: 'left' | 'right'
  speed?: number // Pixels per second for auto-scroll
  className?: string
}

export function ReviewMarquee({
  reviews,
  direction = 'left',
  speed = 50,
  className,
}: ReviewMarqueeProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)

  // Animation state (refs to avoid stale closures)
  const positionRef = useRef(0)        // f - current translateX position
  const isPausedRef = useRef(false)    // v - paused on hover
  const isDraggingRef = useRef(false)  // y - currently dragging
  const dragStartXRef = useRef(0)      // j - mouse X when drag started
  const dragStartPosRef = useRef(0)    // x - position when drag started
  const lastTimeRef = useRef(0)        // w - last frame timestamp
  const animationFrameRef = useRef<number | null>(null)
  const contentWidthRef = useRef(0)    // i - total content width
  const viewportWidthRef = useRef(0)   // g - viewport width

  // Duplicate reviews for seamless infinite scroll
  const duplicatedReviews = [...reviews, ...reviews]

  // Measure content dimensions
  const measureDimensions = useCallback(() => {
    if (!trackRef.current || !containerRef.current) return
    contentWidthRef.current = trackRef.current.scrollWidth
    viewportWidthRef.current = containerRef.current.offsetWidth
  }, [])

  // Main animation loop (reverse engineered from Senja)
  const animate = useCallback(() => {
    if (!trackRef.current) {
      animationFrameRef.current = requestAnimationFrame(animate)
      return
    }

    const now = performance.now()
    const deltaTime = lastTimeRef.current ? (now - lastTimeRef.current) / 1000 : 0
    lastTimeRef.current = now

    // Auto-scroll when not paused and not dragging
    if (!isPausedRef.current && !isDraggingRef.current) {
      const pixelsToMove = speed * deltaTime
      if (direction === 'left') {
        positionRef.current += pixelsToMove
      } else {
        positionRef.current -= pixelsToMove
      }
    }

    // Infinite loop wrapping (Senja style)
    const halfWidth = contentWidthRef.current / 2
    if (positionRef.current >= halfWidth) {
      positionRef.current -= halfWidth
    } else if (positionRef.current < 0) {
      positionRef.current += halfWidth
    }

    // Apply transform (Senja uses translateX)
    trackRef.current.style.transform = `translateX(-${positionRef.current}px)`

    animationFrameRef.current = requestAnimationFrame(animate)
  }, [direction, speed])

  // Initialize on mount
  useEffect(() => {
    measureDimensions()
    window.addEventListener('resize', measureDimensions)

    // Start at beginning or middle depending on direction
    if (direction === 'right') {
      positionRef.current = contentWidthRef.current / 2
    }

    animationFrameRef.current = requestAnimationFrame(animate)

    return () => {
      window.removeEventListener('resize', measureDimensions)
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [animate, measureDimensions, direction])

  // Mouse down - start drag (Senja: E function)
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!containerRef.current || !trackRef.current) return
    isPausedRef.current = true
    isDraggingRef.current = true
    dragStartXRef.current = e.pageX - containerRef.current.offsetLeft
    dragStartPosRef.current = positionRef.current
    trackRef.current.style.cursor = 'grabbing'
  }, [])

  // Mouse move - drag (Senja: M function with 2x multiplier)
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDraggingRef.current || !containerRef.current) return
    e.preventDefault()
    const currentX = e.pageX - containerRef.current.offsetLeft
    const dragDistance = 2 * (currentX - dragStartXRef.current) // 2x multiplier like Senja
    positionRef.current = dragStartPosRef.current - dragDistance
  }, [])

  // Mouse up - end drag (Senja: C function)
  const handleMouseUp = useCallback(() => {
    isDraggingRef.current = false
    if (trackRef.current) {
      trackRef.current.style.cursor = 'grab'
    }
  }, [])

  // Mouse enter - pause (Senja: I function)
  const handleMouseEnter = useCallback(() => {
    isPausedRef.current = true
    if (trackRef.current) {
      trackRef.current.style.cursor = 'grab'
    }
  }, [])

  // Mouse leave - resume (Senja: W function)
  const handleMouseLeave = useCallback(() => {
    isPausedRef.current = false
    isDraggingRef.current = false
    if (trackRef.current) {
      trackRef.current.style.cursor = 'default'
    }
  }, [])

  // Touch start (Senja: N function)
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (!containerRef.current) return
    isPausedRef.current = true
    isDraggingRef.current = true
    dragStartXRef.current = e.touches[0].clientX - containerRef.current.offsetLeft
    dragStartPosRef.current = positionRef.current
  }, [])

  // Touch move (Senja: B function)
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDraggingRef.current || !containerRef.current) return
    const currentX = e.touches[0].clientX - containerRef.current.offsetLeft
    const dragDistance = 2 * (currentX - dragStartXRef.current)
    positionRef.current = dragStartPosRef.current - dragDistance
  }, [])

  // Touch end (Senja: U function)
  const handleTouchEnd = useCallback(() => {
    isDraggingRef.current = false
    isPausedRef.current = false
  }, [])

  return (
    <div
      ref={containerRef}
      className={clsx('relative overflow-hidden', className)}
    >
      {/* Gradient masks for smooth edges */}
      <div className="pointer-events-none absolute left-0 top-0 z-10 h-full w-16 bg-gradient-to-r from-gray-50 to-transparent" />
      <div className="pointer-events-none absolute right-0 top-0 z-10 h-full w-16 bg-gradient-to-l from-gray-50 to-transparent" />

      <div
        ref={trackRef}
        className="flex items-start gap-4 pb-1 cursor-grab select-none"
        style={{ transition: 'height 0.5s' }}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {duplicatedReviews.map((review, idx) => (
          <GoogleReviewCard
            key={`${review.id}-${idx}`}
            review={review}
            aria-hidden={idx >= reviews.length}
          />
        ))}
      </div>
    </div>
  )
}
